import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAdminSessionSummary } from "../../auth/hooks/useAdminSessionSummary";
import type { DashboardProject } from '../types.js';
import {
  fetchDashboardProjectsList,
  PROJECTS_WORKSPACE_TARGET,
  submitDashboardProjectCreate,
  submitDashboardProjectDelete,
} from "../projects/api/projectsApi";
import type { AdminApiResult } from "../../shared/api/types/adminApi";
import {
  buildProjectsSummary,
  filterDashboardProjects,
  mapProjectRecordToViewModel,
  mapProjectRecordsToViewModels,
  mapWorkspaceSnapshotToViewModel,
} from "../projects/mappers/projectsMappers";
import type {
  DashboardProjectCreateMutationResultDto,
  DashboardProjectCreateRequestDto,
  DashboardProjectRecordDto,
  DashboardProjectsListDto,
  ProjectsTabId,
  ProjectsWorkspaceSnapshotViewModel as ProjectsWorkspaceSnapshot,
} from "../projects/types/projects.api";
import {
  isDashboardProjectRecordDto,
  validateDashboardProjectCreateMutationResultDto,
  validateDashboardProjectsListDto,
} from "../projects/types/projects.api";
import { loadProjectsWorkspaceSnapshot } from "../services/dashboardProjectsService";
import {
  getCachedDashboardProjects,
  setCachedDashboardProjects,
  clearCachedDashboardProjects,
} from "./dashboardPreviousDataCache";

const INITIAL_PROJECTS: DashboardProject[] = [];
const INITIAL_WORKSPACE: ProjectsWorkspaceSnapshot = mapWorkspaceSnapshotToViewModel({
  status: "loading",
  error: null,
  companyId: PROJECTS_WORKSPACE_TARGET.companyId,
  productId: PROJECTS_WORKSPACE_TARGET.productId,
  companyName: PROJECTS_WORKSPACE_TARGET.companyName,
  projectName: PROJECTS_WORKSPACE_TARGET.projectName,
  backendState: null,
  previewUrl: null,
  liveUrl: null,
  draftRevision: null,
  draftStepCount: null,
  hasLiveDeployment: false,
  updatedAtLabel: null,
});

export interface UseDashboardProjectsOptions {
  initialProjects?: DashboardProject[];
  loadProjectsData?: () => Promise<AdminApiResult<DashboardProjectsListDto>>;
  createProjectData?: (
    request: DashboardProjectCreateRequestDto,
  ) => Promise<AdminApiResult<DashboardProjectCreateMutationResultDto>>;
  deleteProjectData?: (
    projectId: string,
  ) => Promise<AdminApiResult<{ deleted: boolean }>>;
}

function cloneProjectsForState(
  projects: ReadonlyArray<DashboardProject>,
): DashboardProject[] {
  return projects.map((project) => ({
    ...project,
    collaborators: [...project.collaborators],
  }));
}

export function useDashboardProjects({
  initialProjects = INITIAL_PROJECTS,
  loadProjectsData = fetchDashboardProjectsList,
  createProjectData = submitDashboardProjectCreate,
  deleteProjectData = submitDashboardProjectDelete,
}: UseDashboardProjectsOptions = {}) {
  const { sessionUser } = useAdminSessionSummary();
  const principalKey =
    sessionUser?.id?.trim() || sessionUser?.email?.trim() || "anonymous";
  const [activeTab, setActiveTab] = useState<ProjectsTabId>('all');
  const [projects, setProjects] = useState<DashboardProject[]>(() => {
    const cachedProjects = getCachedDashboardProjects(principalKey);
    return cachedProjects ? cachedProjects : cloneProjectsForState(initialProjects);
  });
  const [isProjectsLoading, setProjectsLoading] = useState(() => true);
  const [projectsErrorMessage, setProjectsErrorMessage] = useState<string | null>(
    null,
  );
  const [isCreatingProject, setCreatingProject] = useState(false);
  const [createProjectErrorMessage, setCreateProjectErrorMessage] = useState<
    string | null
  >(null);
  const createProjectInFlightRef = useRef(false);
  const [projectsLoadVersion, setProjectsLoadVersion] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [workspace, setWorkspace] = useState<ProjectsWorkspaceSnapshot>(INITIAL_WORKSPACE);
  const principalKeyRef = useRef(principalKey);

  useLayoutEffect(() => {
    if (principalKeyRef.current === principalKey) {
      return;
    }

    principalKeyRef.current = principalKey;
    createProjectInFlightRef.current = false; // Reset lock on principal change
    const nextProjects = getCachedDashboardProjects(principalKey);
    setProjects(nextProjects ? nextProjects : cloneProjectsForState(initialProjects));
    setProjectsErrorMessage(null);
    setCreateProjectErrorMessage(null);
    setProjectsLoading(true);
  }, [initialProjects, principalKey]);

  useEffect(() => {
    let disposed = false;

    async function syncProjectsList() {
      // Keep list loading lifecycle inside the hook so sections stay presentational.
      setProjectsLoading(true);

      const result = await loadProjectsData();
      if (!result) {
        setProjectsErrorMessage(
          "Unable to load projects right now.",
        );
        setProjectsLoading(false);
        return;
      }
      console.info("[dashboard.projects] PROJECTS_LIST_RESULT", {
        ok: result.ok,
        itemCount: result.ok ? result.data.items.length : 0,
        errorMessage: result.ok ? null : result.error.message,
      });
      if (disposed) {
        return;
      }

      if (!result.ok) {
        setProjectsErrorMessage(result.error.message);
        setProjectsLoading(false);
        return;
      }

      if (!validateDashboardProjectsListDto(result.data)) {
        setProjectsErrorMessage(
          "Invalid projects list payload. Please retry after backend sync recovers.",
        );
        setProjectsLoading(false);
        return;
      }

      // Map transport DTOs into dashboard project view models once per fetch cycle.
      const nextProjects = mapProjectRecordsToViewModels(result.data.items);
      setProjects(nextProjects);
      setCachedDashboardProjects(principalKey, nextProjects);
      setProjectsErrorMessage(null);
      setProjectsLoading(false);
    }

    void syncProjectsList();

    return () => {
      disposed = true;
    };
  }, [loadProjectsData, principalKey, projectsLoadVersion]);

  useEffect(() => {
    const handleProjectRecordUpdated = (event: Event) => {
      const detail = (event as CustomEvent<DashboardProjectRecordDto>).detail;
      if (!isDashboardProjectRecordDto(detail)) {
        return;
      }

      const nextProject = mapProjectRecordToViewModel(detail);
      setProjects((currentProjects) => {
        const nextProjects = currentProjects.some(
          (project) => project.id === nextProject.id,
        )
          ? currentProjects.map((project) =>
              project.id === nextProject.id ? nextProject : project,
            )
          : [nextProject, ...currentProjects];
        setCachedDashboardProjects(principalKey, nextProjects);
        return nextProjects;
      });
      setProjectsErrorMessage(null);
    };

    window.addEventListener(
      "dashboard-project-record-updated",
      handleProjectRecordUpdated as EventListener,
    );

    return () => {
      window.removeEventListener(
        "dashboard-project-record-updated",
        handleProjectRecordUpdated as EventListener,
      );
    };
  }, [principalKey]);

  const refreshWorkspace = useCallback(async () => {
    setWorkspace((current) => ({
      ...current,
      status: 'loading',
      error: null,
      stateLabel: 'Connecting to viewer control plane',
    }));

    setWorkspace(await loadProjectsWorkspaceSnapshot());
  }, []);

  useEffect(() => {
    void refreshWorkspace();
  }, [principalKey, refreshWorkspace]);

  const retryProjectsLoad = useCallback(() => {
    setProjectsLoadVersion((currentVersion) => currentVersion + 1);
  }, []);

  const removeProject = useCallback(
    (projectId: string) => {
      setProjects((currentProjects) => {
        const nextProjects = currentProjects.filter((p) => p.id !== projectId);
        setCachedDashboardProjects(principalKey, nextProjects);
        return nextProjects;
      });
    },
    [principalKey],
  );

  const deleteProject = useCallback(
    async (projectId: string): Promise<{ ok: boolean; message?: string }> => {
      const trimmed = projectId.trim();
      if (!trimmed) {
        return { ok: true };
      }

      const result = await deleteProjectData(trimmed);
      if (!result.ok) {
        console.error("[dashboard.projects] PROJECT_DELETE_THROW", {
          projectId: trimmed,
          message: result.error.message,
        });
        return { ok: false, message: result.error.message };
      }

      setProjects((currentProjects) => {
        const nextProjects = currentProjects.filter((p) => p.id !== trimmed);
        setCachedDashboardProjects(principalKey, nextProjects);
        return nextProjects;
      });
      return { ok: true };
    },
    [deleteProjectData, principalKey],
  );

  const createProject = useCallback(
    async ({
      collaborators,
      title,
    }: {
      title: string;
      collaborators?: string[];
    }): Promise<DashboardProject | null> => {
      // Use a ref lock so two rapid submits in the same render tick cannot
      // both observe stale React state and create duplicate projects.
      if (createProjectInFlightRef.current) {
        return null;
      }

      createProjectInFlightRef.current = true;
      setCreatingProject(true);
      setCreateProjectErrorMessage(null);
      try {
        console.info("[dashboard.projects] CREATE_PROJECT_INPUT", {
          title,
          collaborators: collaborators ?? [],
          companyName: workspace.companyName,
          workspaceCompanyId: workspace.companyId,
          workspaceProductId: workspace.productId,
        });
        const result = await createProjectData({
          title,
          collaborators,
          companyName: workspace.companyName,
        });
        console.info("[dashboard.projects] PROJECT_CREATE_RESULT", {
          ok: result.ok,
          itemId: result.ok ? result.data.item.id : null,
          itemTitle: result.ok ? result.data.item.title : null,
          errorMessage: result.ok ? null : result.error.message,
        });

        if (!result.ok) {
          setCreateProjectErrorMessage(result.error.message);
          return null;
        }

        if (!validateDashboardProjectCreateMutationResultDto(result.data)) {
          setCreateProjectErrorMessage(
            "Invalid project create payload. Please retry after backend sync recovers.",
          );
          return null;
        }

        const nextProject = mapProjectRecordToViewModel(result.data.item);
        setProjects((currentProjects) => {
          const nextProjects = [nextProject, ...currentProjects];
          setCachedDashboardProjects(principalKey, nextProjects);
          return nextProjects;
        });
        setCreateProjectErrorMessage(null);
        return nextProject;
      } catch (error) {
        console.error("[dashboard.projects] PROJECT_CREATE_THROW", error);
        setCreateProjectErrorMessage(
          error instanceof Error
            ? error.message
            : "Unable to create this project right now.",
        );
        return null;
      } finally {
        createProjectInFlightRef.current = false;
        setCreatingProject(false);
      }
    },
    [createProjectData, principalKey, workspace.companyName],
  );

  const filteredProjects = useMemo(
    () => filterDashboardProjects(projects, activeTab, searchQuery),
    [activeTab, projects, searchQuery],
  );

  const summary = useMemo(
    () => ({
      ...buildProjectsSummary(projects),
      visible: filteredProjects.length,
    }),
    [filteredProjects.length, projects],
  );

  return {
    activeTab,
    createProject,
    createProjectErrorMessage,
    deleteProject,
    filteredProjects,
    isCreatingProject,
    isProjectsLoading,
    projectsErrorMessage,
    refreshWorkspace,
    removeProject,
    retryProjectsLoad,
    searchQuery,
    setActiveTab,
    setSearchQuery,
    summary,
    workspace,
  };
}
